import { type FC, useState } from "react";
import { AlertCircle, Lightbulb05, MessageChatSquare, MessageSmileSquare } from "@untitledui/icons";
import { Heading as AriaHeading, Radio as AriaRadio, RadioGroup as AriaRadioGroup } from "react-aria-components";
import { useLocation } from "react-router";
import { FEEDBACK_KIND_LABELS, FEEDBACK_MAX_CHARS, FEEDBACK_MIN_CHARS, FEEDBACK_STATUS_LABELS } from "@mfa-aff/shared";
import type { FeedbackKindKey, FeedbackStatusKey, PartnerFeedbackView } from "@mfa-aff/shared";
import { Callout, IconChip, JalaliDate, LatinRuns, Skeleton, cx, errorMessage, formatNumber, notify, toPersianDigits } from "@mfa-aff/ui";
import { Dialog, Modal, ModalOverlay } from "@mfa-aff/ui/components/application/modals/modal";
import { BadgeWithDot } from "@mfa-aff/ui/components/base/badges/badges";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { CloseButton } from "@mfa-aff/ui/components/base/buttons/close-button";
import { TextArea } from "@mfa-aff/ui/components/base/textarea/textarea";
import { FeaturedIcon } from "@mfa-aff/ui/components/foundations/featured-icon/featured-icon";
import { ApiError } from "../api/client";
import { useFeedbackList, useFeedbackSubmit } from "../api/panel-content";

/** URL hash that opens the dialog from anywhere (sidebar item, «پاسخ تیم» notifications). */
export const FEEDBACK_HASH = "#feedback";

/** How many earlier messages the dialog lists. */
const RECENT = 3;

const KINDS: { value: FeedbackKindKey; icon: FC<{ className?: string }>; hint: string; placeholder: string }[] = [
    {
        value: "suggestion",
        icon: Lightbulb05,
        hint: "ایده‌ای برای بهتر شدن پنل",
        placeholder: "مثلاً: کاش در صفحهٔ سفارش‌ها می‌شد سفارش‌ها را بر اساس ماه جدا کرد.",
    },
    {
        value: "problem",
        icon: AlertCircle,
        hint: "چیزی درست کار نمی‌کند",
        placeholder: "چه کاری می‌کردید و چه اتفاقی افتاد؟ مثلاً: دکمهٔ ساخت لینک را می‌زنم ولی لینکی ساخته نمی‌شود.",
    },
];

const STATUS_COLOR: Record<FeedbackStatusKey, "brand" | "gray" | "success"> = { new: "brand", seen: "gray", done: "success" };

const KindCard = ({ value, icon, hint }: (typeof KINDS)[number]) => (
    <AriaRadio
        value={value}
        className={({ isSelected, isFocusVisible }) =>
            cx(
                "flex min-w-0 cursor-pointer items-center gap-3 rounded-xl bg-primary p-3 ring-1 ring-secondary transition duration-100 ease-linear ring-inset hover:ring-primary",
                isSelected && "bg-brand-primary_alt ring-2 ring-brand dark:bg-brand-500/10",
                isFocusVisible && "outline-2 outline-offset-2 outline-focus-ring",
            )
        }
    >
        {({ isSelected }) => (
            <>
                <IconChip icon={icon} color={value === "problem" ? "warning" : "brand"} />
                <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-sm font-semibold text-primary">{FEEDBACK_KIND_LABELS[value]}</span>
                    <span className="text-xs text-tertiary">{hint}</span>
                </span>
                <span
                    aria-hidden="true"
                    className={cx("flex size-5 shrink-0 items-center justify-center rounded-full ring-1 ring-primary ring-inset", isSelected && "bg-brand-solid ring-bg-brand-solid")}
                >
                    {isSelected && <span className="size-2 rounded-full bg-white" />}
                </span>
            </>
        )}
    </AriaRadio>
);

const PastMessage = ({ item }: { item: PartnerFeedbackView }) => (
    <li className="flex flex-col gap-2 rounded-xl p-3 ring-1 ring-secondary ring-inset">
        <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-tertiary">
                <span className="font-semibold text-secondary">{FEEDBACK_KIND_LABELS[item.kind]}</span> · <JalaliDate value={item.createdAt} format="short" />
            </p>
            <BadgeWithDot type="pill-color" size="sm" color={STATUS_COLOR[item.status]}>
                {FEEDBACK_STATUS_LABELS[item.status]}
            </BadgeWithDot>
        </div>
        <p className="line-clamp-3 text-sm break-words whitespace-pre-line text-secondary">
            <LatinRuns text={item.body} />
        </p>
        {item.reply && (
            <div className="flex flex-col gap-1 rounded-lg bg-brand-primary_alt p-3 dark:bg-brand-500/10">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-brand-secondary dark:text-primary">
                    <MessageSmileSquare className="size-4 shrink-0" aria-hidden="true" />
                    پاسخ تیم میزبان‌فا
                    {item.repliedAt && (
                        <span className="font-normal text-tertiary">
                            · <JalaliDate value={item.repliedAt} format="short" />
                        </span>
                    )}
                </p>
                <p className="text-sm break-words whitespace-pre-line text-secondary">
                    <LatinRuns text={item.reply} />
                </p>
            </div>
        )}
    </li>
);

/** «پیشنهاد / گزارش مشکل»: a short message to the team (the current page goes with it), and the earlier ones with replies. */
export const FeedbackDialog = ({ isOpen, onOpenChange }: { isOpen: boolean; onOpenChange: (open: boolean) => void }) => {
    const { pathname, search } = useLocation();
    const [kind, setKind] = useState<FeedbackKindKey>("suggestion");
    const [body, setBody] = useState("");
    const [bodyError, setBodyError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const submit = useFeedbackSubmit();
    const list = useFeedbackList(isOpen);

    const length = body.trim().length;
    const tooShort = length < FEEDBACK_MIN_CHARS;
    const current = KINDS.find((k) => k.value === kind) ?? KINDS[0]!;

    const reset = () => {
        setKind("suggestion");
        setBody("");
        setBodyError(null);
        setError(null);
    };
    const close = () => {
        onOpenChange(false);
        reset();
    };

    const send = async () => {
        if (tooShort) {
            setBodyError(`پیام کوتاه است؛ دست‌کم ${toPersianDigits(FEEDBACK_MIN_CHARS)} کاراکتر بنویسید.`);
            return;
        }
        setBodyError(null);
        setError(null);
        try {
            await submit.mutateAsync({ kind, body: body.trim(), pagePath: `${pathname}${search}`.slice(0, 400) });
            notify.success("پیامتان رسید؛ پاسخ تیم میزبان‌فا در اعلان‌ها می‌آید.");
            close();
        } catch (e) {
            if (e instanceof ApiError && e.field === "body") setBodyError(e.message);
            else setError(errorMessage(e));
        }
    };

    const recent = list.data?.slice(0, RECENT) ?? [];

    return (
        <ModalOverlay
            isOpen={isOpen}
            onOpenChange={(open) => (open ? onOpenChange(true) : close())}
            isDismissable={!submit.isPending}
        >
            <Modal className="sm:max-w-140">
                <Dialog aria-label="پیشنهاد یا گزارش مشکل">
                    <div className="relative flex w-full flex-col gap-5 p-4 sm:p-6">
                        <CloseButton onPress={close} size="lg" label="بستن" className="absolute top-3 end-3" />
                        <FeaturedIcon icon={MessageChatSquare} color="brand" theme="light" size="lg" />
                        <div className="flex flex-col gap-1.5 pe-8">
                            <AriaHeading slot="title" className="text-lg font-semibold text-primary">
                                پیشنهاد یا گزارش مشکل
                            </AriaHeading>
                            <p className="text-sm text-tertiary">ایده‌ای دارید یا جایی از پنل درست کار نمی‌کند؟ برای تیم میزبان‌فا بنویسید؛ همهٔ پیام‌ها را می‌خوانیم.</p>
                        </div>

                        <AriaRadioGroup
                            aria-label="نوع پیام"
                            value={kind}
                            onChange={(v) => setKind(v as FeedbackKindKey)}
                            className="grid grid-cols-1 gap-3 xs:grid-cols-2"
                        >
                            {KINDS.map((k) => (
                                <KindCard key={k.value} {...k} />
                            ))}
                        </AriaRadioGroup>

                        <div className="flex flex-col gap-1.5">
                            <TextArea
                                label="پیام شما"
                                rows={5}
                                maxLength={FEEDBACK_MAX_CHARS}
                                placeholder={current.placeholder}
                                value={body}
                                onChange={(v) => {
                                    setBody(v);
                                    setBodyError(null);
                                }}
                                isInvalid={!!bodyError}
                                hint={bodyError ?? `دست‌کم ${toPersianDigits(FEEDBACK_MIN_CHARS)} کاراکتر بنویسید.`}
                            />
                            <div className="flex items-start justify-between gap-3 text-xs">
                                <p className="text-tertiary">صفحه‌ای که در آن هستید هم همراه پیام فرستاده می‌شود تا راحت‌تر پیدایش کنیم.</p>
                                <span className={cx("shrink-0 digits-tabular", body.length >= FEEDBACK_MAX_CHARS ? "text-error-primary" : "text-quaternary")}>
                                    {formatNumber(body.length)} از {formatNumber(FEEDBACK_MAX_CHARS)}
                                </span>
                            </div>
                        </div>

                        {error && (
                            <Callout tone="error" icon={AlertCircle}>
                                {error}
                            </Callout>
                        )}

                        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <Button color="secondary" size="lg" onPress={close} isDisabled={submit.isPending}>
                                انصراف
                            </Button>
                            <Button color="primary" size="lg" onPress={send} isLoading={submit.isPending} showTextWhileLoading isDisabled={length === 0}>
                                فرستادن
                            </Button>
                        </div>

                        {(list.isPending || recent.length > 0) && (
                            <section aria-labelledby="feedback-past" className="flex flex-col gap-3 border-t border-secondary pt-5">
                                <h3 id="feedback-past" className="text-sm font-semibold text-primary">
                                    پیام‌های قبلی شما
                                </h3>
                                {list.isPending ? (
                                    <Skeleton className="h-20 w-full rounded-xl" />
                                ) : (
                                    <ul className="flex flex-col gap-2">
                                        {recent.map((item) => (
                                            <PastMessage key={item.id} item={item} />
                                        ))}
                                    </ul>
                                )}
                            </section>
                        )}
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
